// Interesting take on isolated vs redux components: https://medium.com/netscape/component-state-vs-redux-store-1eb0c929277
// Slow rendering: https://github.com/amcharts/amcharts4/issues/723
import React, { Component } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import { LegendContainer, LineChartContainer } from './styles';
import _ from 'lodash';

import { COLORS, FONT_SIZES } from '../../styles/constants';
import { ID_GRAPH_AXIS } from '../../pageDefinition/constants/idDefinition';
import { sendAdobeEvent } from '../../utils/analytics/adobe';
import { APP_NAME } from '../../utils/constants/general';

import { connect } from 'react-redux';

const mapStateToProps = state => ({
	defaultMappingName: state.designDefinition.partDefinition.defaultMappingName,
	wpn: state.designDefinition.partDefinition.wpn
});

const getLogDecades = (min, max) => {
	let decades = [];
	let start = Math.ceil(Math.log10(min));
	let end = Math.floor(Math.log10(max));
	for (let i = start; i <= end; i++) {
		decades.push(Math.pow(10, i));
	}
	return decades;
};

class LineAmChart extends Component {
	componentDidMount() {
		this.initChart();
	}

	componentDidUpdate(prevProps) {
		// Handle refreshing the chart when the dataset changes
		if (!_.isEqual(prevProps.data, this.props.data)) {
			if (this.chart) {
				this.chart.dispose();
			}

			this.initChart();
		}

		if (!_.isEqual(prevProps.undoClamChecked, this.props.undoClamChecked)) {
			this.chart.xAxes.getIndex(0).zoom({ start: 0, end: 1 });
			this.chart.yAxes.getIndex(0).zoom({ start: 0, end: 1 });

			const xAxis = this.chart.xAxes.getIndex(0);
			const xAxeDef = this.props.chartDescription.axis.find(axe => {
				return axe.id === ID_GRAPH_AXIS.X;
			});
			let clampValueForX =
				!this.props.undoClamChecked &&
				xAxeDef.clampMaxRating &&
				xAxeDef.clampMaxRating.mappingName &&
				this.props.tableData &&
				this.props.tableData[xAxeDef.clampMaxRating.mappingName] &&
				this.props.data.axesValuesRange.x.max >= Number(this.props.tableData[xAxeDef.clampMaxRating.mappingName].value)
					? Number(this.props.tableData[xAxeDef.clampMaxRating.mappingName].value)
					: undefined;
			xAxis.max = clampValueForX;
			xAxis.min = clampValueForX && clampValueForX < this.props.data.axesValuesRange.x.min ? 0 : undefined;

			const yAxis = this.chart.yAxes.getIndex(0);
			const yAxeDef = this.props.chartDescription.axis.find(axe => {
				return axe.id === ID_GRAPH_AXIS.Y1;
			});
			let clampValueForY =
				!this.props.undoClamChecked &&
				yAxeDef.clampMaxRating &&
				yAxeDef.clampMaxRating.mappingName &&
				this.props.tableData &&
				this.props.tableData[yAxeDef.clampMaxRating.mappingName] &&
				this.props.data.axesValuesRange.y.max >= Number(this.props.tableData[yAxeDef.clampMaxRating.mappingName].value)
					? Number(this.props.tableData[yAxeDef.clampMaxRating.mappingName].value)
					: undefined;
			yAxis.max = clampValueForY;
			yAxis.min = clampValueForY && clampValueForY < this.props.data.axesValuesRange.y.min ? 0 : undefined;
		}
	}

	componentWillUnmount() {
		if (this.chart) {
			this.chart.dispose();
		}
	}

	initChart() {
		const { data, chartDescription, displayInput, tableData, undoClamChecked } = this.props;
		// am4core.useTheme(am4themesAnimated);
		am4core.options.commercialLicense = true;
		am4core.addLicense('CH2281-6107-0011-9579');

		this.chart = am4core.create(chartDescription.id, am4charts.XYChart);
		let legendContainer = am4core.create(`${this.props.chartDescription.id}-legend`, am4core.Container);
		legendContainer.width = am4core.percent(100);
		legendContainer.height = am4core.percent(100);

		this.chart.colors.list = [
			am4core.color(COLORS.PRIMARY),
			am4core.color(COLORS.LINK),
			am4core.color(COLORS.BACKGROUND_GREY_DARK),
			am4core.color('green'),
			am4core.color('red'),
			am4core.color('yellow'),
			am4core.color('purple')
		];

		let chartConst = {
			seriesNames: {}
		};

		/* X axis */
		const xAxis = this.chart.xAxes.push(new am4charts.ValueAxis());
		const xAxeDef = chartDescription.axis.find(axe => {
			return axe.id === ID_GRAPH_AXIS.X;
		});
		xAxis.fontSize = FONT_SIZES.BODY2;
		xAxis.title.text = xAxeDef.heading + ' [[' + xAxeDef.unit + ']]';
		xAxis.title.fontSize = FONT_SIZES.BODY1;
		xAxis.title.paddingBottom = 10;
		xAxis.renderer.minGridDistance = 50;
		xAxis.renderer.grid.template.location = 0.5;
		xAxis.data = data.series.length > 0 ? data.series[0].items : [];
		xAxis.numberFormatter = new am4core.NumberFormatter();
		xAxis.numberFormatter.numberFormat = '#.##a';

		let clampValueForX =
			!undoClamChecked &&
			xAxeDef.clampMaxRating &&
			xAxeDef.clampMaxRating.mappingName &&
			tableData &&
			tableData[xAxeDef.clampMaxRating.mappingName] &&
			data.axesValuesRange.x.max >= Number(tableData[xAxeDef.clampMaxRating.mappingName].value)
				? Number(tableData[xAxeDef.clampMaxRating.mappingName].value)
				: undefined;
		xAxis.max = clampValueForX;
		xAxis.min = clampValueForX && clampValueForX < data.axesValuesRange.x.min ? 0 : undefined;
		xAxis.strictMinMax = true;

		/* Y axis */
		const yAxis = this.chart.yAxes.push(new am4charts.ValueAxis());
		const yAxeDef = chartDescription.axis.find(axe => {
			return axe.id === ID_GRAPH_AXIS.Y1;
		});
		yAxis.fontSize = FONT_SIZES.BODY2;
		yAxis.title.text = yAxeDef.heading + (yAxeDef.unit && ' [[' + yAxeDef.unit + ']]');
		yAxis.title.fontSize = FONT_SIZES.BODY1;
		yAxis.numberFormatter = new am4core.NumberFormatter();
		yAxis.numberFormatter.numberFormat = '#.##a';

		let clampValueForY =
			!undoClamChecked &&
			yAxeDef.clampMaxRating &&
			yAxeDef.clampMaxRating.mappingName &&
			tableData &&
			tableData[yAxeDef.clampMaxRating.mappingName] &&
			data.axesValuesRange.y.max >= Number(tableData[yAxeDef.clampMaxRating.mappingName].value)
				? Number(tableData[yAxeDef.clampMaxRating.mappingName].value)
				: undefined;
		yAxis.max = clampValueForY;
		yAxis.min = clampValueForY && clampValueForY < data.axesValuesRange.y.min ? 0 : undefined;
		yAxis.strictMinMax = true;

		/* loglog charts*/
		if (chartDescription.isLogLog) {
			xAxis.logarithmic = true;
			yAxis.logarithmic = true;
			xAxis.renderer.labels.template.disabled = true;
			yAxis.renderer.labels.template.disabled = true;
			xAxis.renderer.grid.template.disabled = true;
			yAxis.renderer.grid.template.disabled = true;
			let allItems = [];
			for (const series of data.series) {
				allItems.push(...series.items.filter(item => item.x > 0 && item.y > 0));
			}
			const xValues = allItems.map(item => item.x);
			const yValues = allItems.map(item => item.y);
			const xAxisMin = Math.min(...xValues);
			const xAxisMax = Math.max(...xValues);
			const yAxisMin = Math.min(...yValues);
			const yAxisMax = Math.max(...yValues);
			const xdecades = getLogDecades(xAxisMin, xAxisMax);
			const ydecades = getLogDecades(yAxisMin, yAxisMax);
			xdecades.forEach(value => {
				let range = xAxis.axisRanges.create();
				range.value = value;
				range.label.text = this.chart.numberFormatter.format(value, '#.##a');
			});
			ydecades.forEach(value => {
				let range = yAxis.axisRanges.create();
				range.value = value;
				range.label.text = this.chart.numberFormatter.format(value, '#.##a');
			});
			xAxis.extraTooltipPrecision = xAxisMax <= 0.01 ? 1 : 2;
		}

		for (let i = 0; i < data.series.length; ++i) {
			const series = this.chart.series.push(new am4charts.LineSeries());
			series.showOnInit = false; // ?
			series.dataFields.valueY = 'y';
			series.dataFields.valueX = 'x';
			series.name = chartConst.seriesNames[data.series[i].seriesName]
				? chartConst.seriesNames[data.series[i].seriesName]
				: data.series[i].label +
				  ' ' +
				  parseFloat(data.series[i].seriesName).toFixed(2) +
				  (chartDescription.series.unit && ' [[' + chartDescription.series.unit + ']]');

			if (chartDescription.convertYAxisToMili) {
				series.data = data.series[i].items.map(item => {
					return {
						...item,
						y: item.y * 1000
					};
				});
			} else if (chartDescription.isLogLog) {
				series.data = data.series[i].items.filter(item => item.x > 0 && item.y > 0);
			} else {
				series.data = data.series[i].items;
			}

			const bullet = series.bullets.push(new am4charts.CircleBullet());
			bullet.circle.radius = 2;
			bullet.circle.strokeWidth = 2;
			const valueFormatter = new am4core.NumberFormatter();
			valueFormatter.numberFormat = '#.## a';
			const seriesNameWithoutBrackets = chartConst.seriesNames[data.series[i].seriesName]
				? chartConst.seriesNames[data.series[i].seriesName]
				: data.series[i].label +
				  ' ' +
				  valueFormatter.format(data.series[i].seriesName) +
				  (chartDescription.series.unit && ' ' + chartDescription.series.unit);

			bullet.tooltipText =
				(xAxeDef.heading === 'analysis runs' ? 'analysis run' : xAxeDef.heading) +
				' = ' +
				'{valueX.formatNumber("' +
				valueFormatter.numberFormat +
				'")}' +
				(this.props.chartDescription.simulationType === 'Montecarlo_DC' ? '' : xAxeDef.unit) +
				'\n ' +
				yAxeDef.heading +
				' = ' +
				'{valueY.formatNumber("' +
				valueFormatter.numberFormat +
				'")}' +
				yAxeDef.unit +
				'\n ' +
				(this.props.chartDescription.chartType === 'ComposedChart' ? '' : chartDescription.series.heading + ' = ') +
				seriesNameWithoutBrackets;
		}
		let label = this.chart.chartContainer.createChild(am4core.Label);
		label.text = chartDescription.series.heading;
		label.align = 'Left';
		label.fontSize = FONT_SIZES.BODY1;

		/* Legend */
		this.chart.legend = new am4charts.Legend();
		this.chart.legend.useDefaultMarker = true;
		this.chart.numberFormatter.numberFormat = '.#a';
		this.chart.legend.parent = legendContainer;
		const marker = this.chart.legend.markers.template.children.getIndex(0);
		marker.cornerRadius(1.2, 1.2, 1.2, 1.2);
		marker.width = 15;
		marker.height = 15;

		const updateLegendHeight = () => {
			document.getElementById(`${this.props.chartDescription.id}-legend`).style.height =
				this.chart.legend.contentHeight + 'px';
		};
		this.chart.events.on('datavalidated', updateLegendHeight);
		this.chart.events.on('maxsizechanged', updateLegendHeight);
		this.chart.legend.events.on('datavalidated', updateLegendHeight);
		this.chart.legend.events.on('maxsizechanged', updateLegendHeight);
		this.chart.legend.scrollable = true;

		/* Cursor */
		this.chart.cursor = new am4charts.XYCursor();
		this.chart.cursor.lineX.strokeOpacity = 0.25;
		this.chart.cursor.behavior = 'zoomXY';
		this.chart.cursor.lineY.strokeOpacity = 0.25;

		function formatString(str) {
			if (!str) return '';
			str = str.replace(/<sub>/g, '[baseline-shift: sub; font-size: 11px;]');
			str = str.replace(/<\/sub>/g, '[/]');
			return str;
		}

		/* Title */
		const title = this.chart.titles.create();
		if (displayInput) {
			title.text =
				chartDescription.mainHeading +
				` @ ${formatString(displayInput.heading + '</sub>')} = ${displayInput.value} ${displayInput.unit}`;
		} else {
			title.text = chartDescription.mainHeading;
		}
		title.fontSize = FONT_SIZES.SUBTITLE2;
		title.marginBottom = 30;

		/* Zoom out button */
		this.chart.zoomOutButton.background.cornerRadius(1.2, 1.2, 1.2, 1.2);
		this.chart.zoomOutButton.background.fill = am4core.color(COLORS.PRIMARY);
		this.chart.zoomOutButton.icon.stroke = am4core.color('rgb(250,250,250)');
		this.chart.zoomOutButton.icon.strokeWidth = 2;
		this.chart.zoomOutButton.background.states.getKey('hover').properties.fill = am4core.color(COLORS.PRIMARY);
		this.chart.zoomOutButton.background.states.getKey('down').properties.fill = am4core.color(COLORS.PRIMARY2);

		/* Graph frame styling */
		this.chart.plotContainer.background.strokeWidth = 0.5;
		this.chart.plotContainer.background.strokeOpacity = 1;
		this.chart.plotContainer.background.stroke = am4core.color('black');

		/* Exporting */
		this.chart.exporting.filePrefix =
			'SPICE Live Model chart - ' + chartDescription.chartType + ' - ' + chartDescription.simulationType;

		this.chart.exporting.adapter.add('pdfmakeDocument', (pdf, target) => {
			let seriesCount = this.chart.series.length;
			const height = 800;
			const baseWidth = 155;
			let width = baseWidth * seriesCount;

			pdf.doc.pageOrientation = 'landscape';
			pdf.doc.pageSize = { width: width, height: height };
			pdf.doc.content[0].layout = 'lightHorizontalLines';
			return pdf;
		});

		this.chart.exporting.extraSprites.push({
			sprite: legendContainer,
			position: 'bottom',
			marginTop: 8
		});

		this.chart.exporting.menu = new am4core.ExportMenu();
		this.chart.exporting.menu.closeDelay = 500;
		this.chart.exporting.menu.items[0].label = '';
		this.chart.exporting.menu.items[0].icon =
			'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGAAAABgBAMAAAAQtmoLAAAAHlBMVEUAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAC3KG9qAAAACnRSTlMA/0sEDFdvq8OPbB5ywwAAAMVJREFUeAHt0YEFwwAYROGoKAE8SHWLjBFAR0hnyATdIN245HBBm0MbqP8AeD5cc+xqNYBfBRVUUEEFFZyGbdDeYnCft8HYN2FnLoOD9sGUAJgdjNAngJUAWAGYErASACuQiCciAARw3Q06RAAIYNk5ygTAFgB4E5gAMPAxMAFgYC8Q4S0xEAEgIAYiDORAhIEciBAQAxMGctAZyIEJAznoDOTAhIEcdAJyYEJADkwYiIEIAzEQYSAH2p8GFeQdH1RQq329F+enTePQEFWtAAAAAElFTkSuQmCC';

		function cleanSeriesName(name) {
			name = name.replace(/baseline-shift: sub; font-size: 11px;/g, '');
			return name.trim();
		}

		this.chart.exporting.adapter.add('data', () => {
			const seriesData = [];

			let xValues = [];
			this.chart.series.each(series => {
				series.data.forEach(item => {
					if (xValues.indexOf(item[series.dataFields.valueX]) === -1) {
						xValues.push(item[series.dataFields.valueX]);
					}
				});
			});

			xValues.forEach(x => {
				const dataObj = { Series: x };
				this.chart.series.each(series => {
					const seriesItem = series.data.find(item => item[series.dataFields.valueX] === x);
					const seriesName = chartConst.seriesNames[series.name]
						? chartConst.seriesNames[series.name]
						: cleanSeriesName(series.name);

					dataObj[seriesName] = seriesItem ? seriesItem[series.dataFields.valueY] : null;
				});
				seriesData.push(dataObj);
			});

			const fields = ['Series'].concat(
				this.chart.series.values.map(series => {
					const seriesName = chartConst.seriesNames[series.name]
						? chartConst.seriesNames[series.name]
						: series.name + (chartDescription.series.unit && ' [[' + chartDescription.series.unit + ']]');
					return seriesName;
				})
			);

			return {
				data: seriesData,
				fields: fields.map(value => ({ label: value, value }))
			};
		});

		this.chart.exporting.events.on('exportfinished', event => {
			sendAdobeEvent(
				'Chart',
				window.location.href,
				APP_NAME + ' | ' + this.props.defaultMappingName,
				null,
				this.props.simulationName + ' - ' + this.props.chartDescription.mainHeading + ' - ' + event.format,
				this.props.wpn
			);
		});
	}

	render() {
		return (
			<>
				<LineChartContainer id={this.props.chartDescription.id} className="linechart-container" />
				<LegendContainer id={`${this.props.chartDescription.id}-legend`} className="linechart-label-container" />
			</>
		);
	}
}

export default connect(mapStateToProps)(LineAmChart);
